// Search-optimised titles and descriptions per article.
// Titles are kept under ~60 characters and descriptions under ~155 so they
// render in full in search results and social previews.
export type PostSeo = { title: string; description: string };

export const postSeo: Record<string, PostSeo> = {
  "working-partner-vs-silent-partner": { title: "Working Partner vs Silent Partner: Pay and Profit | Ajility", description: "When one partner runs the business and the other owns half of it. How to separate the reward for working from the return on owning." },
  "founder-bottleneck": {
    title: "Founder Bottleneck: When Every Decision Comes Back | Ajility",
    description:
      "Founder-led businesses stall on structure, not vision. How the bottleneck forms and what it takes to clear it.",
  },
  "fractional-coo-cost-australia": {
    title: "Fractional COO Cost in Australia (2026) | Ajility",
    description:
      "Australian fractional COO pricing for 2026: day rates, monthly retainers, fixed-scope projects, and how they compare with a full-time COO.",
  },
  "transport-118-percent": {
    title: "118% Profit Growth in Transport & Logistics | Ajility",
    description:
      "How four operating levers turned fast revenue growth into 118% net profit growth over four years in a freight business.",
  },
  "mining-90-days": {
    title: "Cutting $1M of Overhead in 90 Days | Ajility",
    description:
      "How a 90-day commercial reset removed about $1M of annualised overhead and secured a multi-million-dollar contract variation.",
  },
  "electrical-margins": {
    title: "Project Financial Discipline in Contracting | Ajility",
    description:
      "An electrical contractor won the work but could not see which jobs made money. How project-level financial discipline rebuilt margins.",
  },
  "healthcare-integrator": {
    title: "Scaling a Founder-Led Healthcare Business | Ajility",
    description:
      "A clinician-founded business had vision and momentum but no operator. How a Fractional Integrator turned strategy into an executable cadence.",
  },
  "fractional-vs-fulltime": {
    title: "Fractional COO vs Full-Time COO Economics | Ajility",
    description:
      "Why a full-time COO is the wrong-shaped tool for most $3M to $30M businesses, and how the fractional economics actually stack up.",
  },
  "strategy-execution-90-day-rhythm": {
    title: "90-Day Operating Rhythm for Strategy Execution | Ajility",
    description:
      "Annual plans drift inside a quarter. The 90-day operating rhythm that keeps priorities tight, owners accountable and the team focused.",
  },
  "pl-visibility": {
    title: "From Gut-Feel to P&L Visibility | Ajility",
    description:
      "Most founder-led SMEs watch revenue, not margin. How to install P&L visibility and pricing discipline, and what it is worth.",
  },
  "eos-implementer-vs-fractional-coo": {
    title: "EOS Implementer vs Fractional COO: Which You Need | Ajility",
    description:
      "One facilitates the system and leaves the room. The other runs the business between meetings. An even-handed comparison, with costs.",
  },
  "fractional-coo-vs-consultant-vs-coach": {
    title: "Fractional COO vs Consultant vs Coach Explained | Ajility",
    description:
      "What each profession is accountable for, typical Australian fees, and which one fits the problem your business actually has.",
  },
  "when-to-hire-a-coo": {
    title: "When to Hire a COO: A Decision Framework | Ajility",
    description:
      "Seven signals the operating seat is vacant, the revenue thresholds that matter, and how to choose between full-time, fractional and promoting internally.",
  },
  "right-people-right-seats": {
    title: "Accountability Chart: Right People, Right Seats | Ajility",
    description:
      "Good people go quiet when they are unclear on what they own. It reads as a people problem. It is almost always a structure problem.",
  },
  "pricing-engine-kmart-tyre-auto": {
    title: "What a Tyre Pricing Strategy Is Worth | Ajility",
    description:
      "Inside a 249-store network: how price architecture, labour rates and a pricing rhythm turned one review cycle into a $6M first-year revenue uplift.",
  },
  "pricing-tools-coca-cola-amatil": {
    title: "Pricing Tools a $5 Billion Business Runs On | Ajility",
    description:
      "How a fragmented spreadsheet estate became one governed pricing platform, with deals modelled before signing and credits checked against pattern.",
  },
  "hire-a-general-manager-or-a-fractional-one": {
    "title": "Hire a General Manager or a Fractional One? | Ajility",
    "description": "Full-time GM or fractional GM for a $3M to $30M Australian business: four questions that decide it, how to spot an operator, and the trades version."
  },
  "how-to-step-back-from-sales-in-your-own-business": {
    "title": "How to Step Back From Sales in Your Own Business | Ajility",
    "description": "A founder's order of work for stepping back from sales: delegate decisions before tasks, install the scorecard, then give it two quarters to settle in."
  },
  "how-to-restructure-a-small-business-group-with-shared-owners": {
    "title": "Restructure a Business Group With Shared Owners | Ajility",
    "description": "Operating restructure for an Australian owner-operated group: shared services, the accountability chart, the top-heavy test and a review before redundancy."
  },
  "13-week-cash-flow-forecast": {
    "title": "13-Week Cash Flow Forecast: How to Build One | Ajility",
    "description": "What a 13-week cash flow forecast is, what goes in it, how an owner-led Australian business builds one in a week and who should own it."
  },
  "revenue-leakage": {
    "title": "Revenue Leakage: Where the Money Goes Missing | Ajility",
    "description": "Revenue leakage is the gap between what you should have billed and what you banked. Where it hides, finding it in a fortnight, why it beats a price rise."
  },
  "commission-and-bonus-structure-design": {
    "title": "Commission and Bonus Structure Design for SMEs | Ajility",
    "description": "How a $3M to $30M Australian business designs a commission or bonus scheme that pays for margin, not just revenue, and fixes a scheme that costs money."
  },

  "payday-super-cash-flow": {
    "title": "Payday Super and Small Business Cash Flow | Ajility",
    "description": "Payday Super started 1 July 2026: super reaches the fund within 7 business days of each payday. What it does to small business cash flow and forecasting."
  },
  "card-surcharge-ban-pricing": {
    "title": "Card Surcharge Ban: How to Price the Fee Back In | Ajility",
    "description": "Card surcharges were banned from 1 October 2026. The fee moved into your margin. How an Australian small business sizes the hit and prices it back in."
  },
  "ai-adoption-small-business": {
    "title": "AI Adoption in Small Business: Why Staff Avoid It | Ajility",
    "description": "Bought AI licences and nothing changed? Why staff won't use new systems, which processes to automate first, and a rollout an Australian SME will use."
  },
  "small-business-succession-planning": {
    "title": "Small Business Succession Planning in Australia | Ajility",
    "description": "Small business succession planning in Australia: the market salary test, what buyers look for, family succession and a three-to-five-year plan."
  },
  "security-of-payment-act-subcontractors": {
    "title": "Security of Payment Act for Subcontractors | Ajility",
    "description": "How the Security of Payment Act works for Australian subcontractors, why builder failures hit trades first, and six claim habits that protect cash."
  },
};

export const seoTitleFor = (slug: string, fallback: string) =>
  postSeo[slug]?.title ?? `${fallback} | Ajility`;

export const seoDescriptionFor = (slug: string, fallback: string) =>
  postSeo[slug]?.description ?? fallback;
